import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Avatar,
  Tooltip,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Rating,
  Modal,
  Card,
  CardContent,
  Paper,
  TablePagination, Grid, TextField, FormControl, InputLabel, Select, MenuItem, Collapse,Chip
} from '@mui/material';
import {
  CheckCircle,
  AccessTime,
  AssignmentTurnedIn,
  Group,
  History, Search,
  PostAdd, Edit as EditIcon
} from "@mui/icons-material";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import AddPerformanceReviewAndRatings from './AddPerformanceReviewAndRatings';
import axios from 'axios';
import { localAxiosGet } from '../../../Axios/axios';
import EditPerformanceReviewAndRatings from "./EditPerformanceandRatings";
import PastApparaisalOfEmployee from './PastApparaisalOfEmployee';


const EmployeePerformanceReview = () => {
  const [employees, setEmployees] = useState([]);
  const [stats, setStats] = useState({
    all_employees: 0,
    pending_review: 0,
    completed: 0,
    acknowledged: 0
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [isAddAppraisalsModalOpen, setIsAddAppraisalsModalOpen] = useState(false);
  const [isPastAppraisalsModalOpen, setIsPastAppraisalsModalOpen] = useState(false);
  const [employeeForPastAppraisals, setEmployeeForPastAppraisals] = useState(null);
  const [appraisalPeriod, setAppraisalPeriod] = useState('Yearly');

  const [selectedCardFilter, setSelectedCardFilter] = useState(null);
  const [selectedemployeeId, setSelectedEmployeeId] = useState('')

  // Pagination states for table
  const [tablePage, setTablePage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const userId = localStorage.getItem('userId'); 
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  


  useEffect(() => {
    const fetchEmployees = async () => {
      setLoading(true);
      try {
        const response = await axios(
          localAxiosGet(
            `/performancemanagement/getAppraisalsByStatus?report_manager_id=${userId}&goal_period=Yearly`
          )
        );

        const formatted = (response.data.result || []).map((item) => ({
          id: item.id,
          name: `${item.first_name || ''} ${item.last_name || ''}`.trim(),
          position: item.designation || "N/A",
          department: item.department || "N/A",
          appraisals: {
            Yearly: {
              completed: item.is_appraisal,
              feedback: "",
            },
          },
          is_appraisal:item.is_appraisal,
          status: item.is_appraisal ? "completed" : "pending_review",
        }));

        setEmployees(formatted);
      } catch (err) {
        setError("Failed to load employees.");
      } finally {
        setLoading(false);
      }
    };
    fetchEmployees();
  }, []);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios(
          localAxiosGet(
            `/performancemanagement/getAppraisalDashboardStats?reporting_manager_id=${userId}`
          )
        );
        const res = response.data.result || {};
        setStats({
          all_employees: res.all_employees ?? 0,
          pending_review: res.in_progress ?? 0,
          completed: res.completed ?? 0,
          acknowledged: res.acknowledged ?? 0,
        });
      } catch (err) {
        console.error("Error fetching dashboard stats:", err);
      }
    };
    fetchStats();
  }, []);

 
  

  const cardCategories = [
    {
      key: 'all',
      title: 'All Employees',
      icon: Group,
      colors: {
        background: 'linear-gradient(135deg, #E3F2FD 0%, #BBDEFB 100%)',
        icon: '#2196F3',
        text: '#2196F3',
        border: '#2196F3'
      },
      getCount: () => stats.all_employees,
      filterLogic: () => true
    },
    {
      key: 'pending',
      title: 'Pending Review',
      icon: AccessTime,
      colors: {
        background: 'linear-gradient(135deg, #FFF8E1 0%, #FFECB3 100%)',
        icon: '#FF9800',
        text: '#FF9800',
        border: '#FF9800'
      },
      getCount: () => stats.pending_review,
      filterLogic: (employee) =>
        employee.status === 'pending_review' || employee.status === 'in_progress'
    },
    {
      key: 'completed',
      title: 'Completed',
      icon: CheckCircle,
      colors: {
        background: 'linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)',
        icon: '#4CAF50',
        text: '#4CAF50',
        border: '#4CAF50'
      },
      getCount: () => stats.completed,
      filterLogic: (employee) => employee.status === 'completed'
    },
    {
      key: 'acknowledged',
      title: 'Acknowledged',
      icon: AssignmentTurnedIn,
      colors: {
        background: 'linear-gradient(135deg, #E0F2F7 0%, #B2EBF2 100%)',
        icon: '#00BCD4',
        text: '#00BCD4',
        border: '#00BCD4'
      },
      getCount: () => stats.acknowledged,
      filterLogic: (employee) => employee.status === 'acknowledged'
    },
  ];

  const handleSearchChange = (event) => setSearchQuery(event.target.value);
  const handleFilterChange = (event) => setFilterStatus(event.target.value);

  const renderAppraisalStatusIcon = (isCompleted) => {
    const iconColor = isCompleted ? '#FFD700' : '#E0E0E0';
    return <CheckCircle sx={{ fontSize: 20, color: iconColor }} />;
  };

  const handleOpenEdit = (employeeId) => {  
    setSelectedEmployeeId(employeeId) 
    setIsEditModalOpen(true);   
  };

  const handleCloseEdit = () => {
    setSelectedEmployeeId(null);
    setIsEditModalOpen(false);
  };

  const handleViewPerformanceClick = (employeeId) => {
    setIsAddAppraisalsModalOpen(true);
    setSelectedEmployeeId(employeeId)
  };

  const handleAppraisalPeriodChange = (event) => {
    setAppraisalPeriod(event.target.value)
  }

  const handleViewPastAppraisalsClick = (employee) => {
    const employeeId = employee?.id;
    setEmployeeForPastAppraisals(employee);
    setSelectedEmployeeId(employeeId)
    setIsPastAppraisalsModalOpen(true);
  };

 


  const filteredEmployees = useMemo(() => {
    return employees.filter((employee) => {
      const matchesSearch = searchQuery === '' ||
        employee.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        employee.position.toLowerCase().includes(searchQuery.toLowerCase()) ||
        employee.department.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesStatus = filterStatus === 'All' || employee.status === filterStatus;
      const matchesCardFilter = !selectedCardFilter ||
        cardCategories.find(card => card.key === selectedCardFilter)?.filterLogic(employee);

      return matchesSearch && matchesStatus && matchesCardFilter;
    });
  }, [searchQuery, filterStatus, appraisalPeriod, selectedCardFilter, employees]);

  const paginatedEmployees = filteredEmployees.slice(tablePage * rowsPerPage, tablePage * rowsPerPage + rowsPerPage);

  return (
    <div>
      {/* ✅ Overview Cards (your original UI preserved) */}
      <Box sx={{ my: 2 }}>
        <Grid container spacing={3} mb={2}>
          {cardCategories.map((card) => {
            const IconComponent = card.icon; // Get the actual icon component
            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={card.key}>
                <Card
                  sx={{
                    boxShadow: 3,
                    borderRadius: 2,
                    background: card.colors.background,
                    height: '100%',
                    cursor: 'pointer',
                    border:
                      selectedCardFilter === card.key
                        ? `2px solid ${card.colors.border}`
                        : 'none',
                  }}
                  onClick={() =>{
                    setSelectedCardFilter(
                      selectedCardFilter === card.key ? null : card.key
                    );
                    setFilterStatus('All')
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', p: 3 }}>
                    <IconComponent sx={{ fontSize: 50, color: card.colors.icon }} />
                    <Typography variant="h6" sx={{ mt: 2, fontWeight: 'bold' }}>
                      {card.title}
                    </Typography>
                    <Typography
                      variant="h3"
                      sx={{ mt: 1, fontWeight: 'bold', color: card.colors.text }}
                    >
                      {card.getCount()}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mt: 3, mb: 3 }}>
        <TextField
          placeholder="Search employees..."
          variant="outlined"
          size="small"
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: <Search sx={{ color: 'action.active', mr: 1 }} />,
          }}
          sx={{ width: 250 }}
        />

        <FormControl variant="outlined" size="small" sx={{ minWidth: 150 }}>
          <InputLabel id="status-filter-label">Status</InputLabel>
          <Select
            labelId="status-filter-label"
            id="status-filter"
            value={filterStatus}
            onChange={handleFilterChange}
            label="Status"
          >
            <MenuItem value="All">All Statuses</MenuItem>
            <MenuItem value="pending_review">Pending Review</MenuItem>
            <MenuItem value="in_progress">In Progress</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="acknowledged">Acknowledged</MenuItem>
          </Select>
        </FormControl>

        {/* Appraisal Period Filter */}
        <FormControl variant="outlined" size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="appraisal-period-label">Appraisal Period</InputLabel>
          <Select
            labelId="appraisal-period-label"
            id="appraisal-period-filter"
            value={appraisalPeriod}
            onChange={(event)=>handleAppraisalPeriodChange(event)}
            label="Appraisal Period"
          >
            <MenuItem value="Quarterly">Quarterly</MenuItem>
            <MenuItem value="HalfYearly">Half-yearly</MenuItem>
            <MenuItem value="Yearly">Yearly</MenuItem>
          </Select>
        </FormControl>

        {/* Review Submitted Checkbox Filter */}
        {/* <FormControlLabel
    control={
      <Checkbox
        checked={showSubmittedOnly}
        onChange={handleShowSubmittedOnlyChange}
      />
    }
    label="Review Submitted"
    sx={{ ml: 1 }}
  /> */}
      </Box>


      {/* Employee Table */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2, boxShadow: 3, mb: 3 }}>
        <TableContainer sx={{ mt: 2, overflowX: 'auto' }}>
          <Table>
            <TableHead sx={{ backgroundColor: '#E3F2FD' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Employee</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Position</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Department</TableCell>
                <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Review Submitted</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedEmployees.map((employee) => (
                <TableRow key={employee.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar alt={employee.name} sx={{ mr: 2 }}>
                        {employee.name.charAt(0)}
                      </Avatar>
                      <Typography variant="body2" fontWeight="medium">
                        {employee.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{employee.position}</TableCell>
                  <TableCell>{employee.department}</TableCell>
                  <TableCell align="center">
                    {/* {employee.appraisals?.[appraisalPeriod]
                      ? renderAppraisalStatusIcon(employee.appraisals[appraisalPeriod].completed)
                      : renderAppraisalStatusIcon(false)} */}
                       {renderAppraisalStatusIcon(employee.is_appraisal)}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {/* Add Appraisals Icon */}
                      <Tooltip title="Add Appraisals" arrow>
                        <IconButton
                          color="primary"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleViewPerformanceClick(employee.id);
                          }}
                        >
                          <PostAdd sx={{ color: '#1976D2' }} />
                        </IconButton>
                      </Tooltip>

                      {/* Past Appraisals Icon */}
                      <Tooltip title="Past Appraisals" arrow>
                        <IconButton
                          color="secondary"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleViewPastAppraisalsClick(employee);
                          }}
                        >
                          <History sx={{ color: '#FF9800' }} />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Edit Appraisal">
                        <IconButton
                          color="primary"
                          onClick={() => handleOpenEdit(employee.id)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Table Pagination */}
        <TablePagination
          component="div"
          count={filteredEmployees.length}
          page={tablePage}
          onPageChange={(e, newPage) => setTablePage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setTablePage(0);
          }}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </Paper>

      {/* Add Appraisals Modal */}
      <Modal open={isAddAppraisalsModalOpen} onClose={() => setIsAddAppraisalsModalOpen(false)}>
        <Box sx={{
          maxWidth: '80vw',
          maxHeight: '100vh',
          margin: 'auto',
          backgroundColor: 'rgba(255,255,255,0.95)',
          borderRadius: 3,
          boxShadow: 3,
          display: 'flex',
          flexDirection: 'column',
        }}>
          <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
            <AddPerformanceReviewAndRatings selectedemployeeId={selectedemployeeId} onClose={() => setIsAddAppraisalsModalOpen(false)} />
          </Box>
          <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', textAlign: 'right' }}>
            <Button variant="outlined" onClick={() => setIsAddAppraisalsModalOpen(false)}>Close</Button>
          </Box>
        </Box>
      </Modal>


      {/* Past Appraisals Modal */}
      <PastApparaisalOfEmployee 
       open={isPastAppraisalsModalOpen}
       onClose={() => setIsPastAppraisalsModalOpen(false)} 
       selectedemployeeId ={selectedemployeeId}
       employeeName ={employeeForPastAppraisals?.name}
       />
     

      <EditPerformanceReviewAndRatings       
        open={isEditModalOpen}
        employeeId={selectedemployeeId}
        onClose={handleCloseEdit}
      />
    </div>
  );
};

export default EmployeePerformanceReview;
